import { formatDisplayValue } from '@/constants/common.constant'
import { themeClassName } from '@/utils/grid-utils/ag-grid-config'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { ColDef, GridReadyEvent } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { Box, Button, Typography } from '@mui/material'
import React, { useCallback, useMemo, useRef } from 'react'

interface TrasReportProps {
  securities?: any[]
  loading?: boolean
  onSave: () => void
}

const TrasReport: React.FC<TrasReportProps> = ({ securities = [], loading = false, onSave }) => {

  // Filter and sort securities to show only those with What-If changes (BUY/SELL transactions)
  const filteredSecurities = useMemo(() => {
    return securities
      .filter(security => {
        const whatIfHoldings = security.holdings_whatif || 0
        // Only exclude rows with zero what-if holdings (allow cash securities)
        return whatIfHoldings !== 0
      })
      .sort((a, b) => {
        const aIsCash = a.secid === 'USDCASH1'
        const bIsCash = b.secid === 'USDCASH1'
        const aType = (a.holdings_whatif || 0) > 0 ? 'BUY' : 'SELL'
        const bType = (b.holdings_whatif || 0) > 0 ? 'BUY' : 'SELL'
        
        // First sort by BUY/SELL type
        const typeComparison = aType.localeCompare(bType)
        if (typeComparison !== 0) return typeComparison
        
        // If same type, cash comes first within that type
        if (aIsCash && !bIsCash) return -1
        if (!aIsCash && bIsCash) return 1
        
        return 0
      })
  }, [securities])

  const securitiesColumnDefs: ColDef[] = useMemo(() => [
    {
      headerName: 'Type',
      width: 150,
      pinned: 'left',
      valueGetter: (params) => {
        const currentHoldings = params.data?.holdings_current || 0
        const whatIfHoldings = params.data?.holdings_whatif || 0
        if (whatIfHoldings === 0) {
          return ''
        }
        return whatIfHoldings > 0 ? 'BUY' : 'SELL'
      },
      cellStyle: { textAlign: 'left' },
      headerClass: 'ag-header-cell-left'
    },
    {
      headerName: 'Security ID',
      field: 'secid',
      width: 150,
      cellStyle: { textAlign: 'left' },
      headerClass: 'ag-header-cell-left'
    },
    {
      headerName: 'Units',
      width: 150,
      valueGetter: (params) => {
        const whatIfHoldings = params.data?.holdings_whatif || 0
        return Math.abs(whatIfHoldings)
      },
      valueFormatter: (params) => {
        return params.value ? params.value.toLocaleString() : '0'
      },
      cellStyle: (params) => {
        const whatIfHoldings = params.data?.holdings_whatif || 0
        const isBuy = whatIfHoldings > 0
        return {
          textAlign: 'right',
          color: isBuy ? '#28a745' : '#dc3545',  // Green for BUY, Red for SELL
          fontWeight: 'bold'
        }
      },
      headerClass: 'ag-header-cell-right'
    },
    {
      headerName: 'Description',
      field: 'description',
      width: 150,
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'ag-header-cell-left'
    },
    {
      headerName: 'Price',
      field: 'price',
      width: 150,
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined || params.value === '') {
          return formatDisplayValue(params.value)
        }
        const num = Number(params.value)
        if (Number.isNaN(num)) {
          return formatDisplayValue(params.value)
        }
        return num.toFixed(2)
      },
      cellStyle: { textAlign: 'right' },
      headerClass: 'ag-header-cell-right'
    },
    {
      headerName: 'Coupon',
      field: 'coup',
      width: 150,
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined || params.value === '') {
          return '0.00'
        }
        const num = Number(params.value)
        if (Number.isNaN(num)) {
          return '0.00'
        }
        return num.toFixed(2)
      },
      cellStyle: { textAlign: 'right' },
      headerClass: 'ag-header-cell-right'
    },
    {
      headerName: 'Maturity',
      field: 'matu',
      width: 150,
      valueFormatter: (params) => {
        if (!params.value) return ''
        const dateStr = params.value.toString()
        if (dateStr.length === 8) {
          // Convert yyyymmdd to yyyy-mm-dd
          const year = dateStr.substring(0, 4)
          const month = dateStr.substring(4, 6)
          const day = dateStr.substring(6, 8)
          return `${year}-${month}-${day}`
        }
        return dateStr
      },
      cellStyle: { textAlign: 'right' },
      headerClass: 'ag-header-cell-right'
    },
    {
      headerName: 'Market Value',
      field: 'mkval',
      width: 150,
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined || params.value === '') {
          return '--'
        }
        const num = Number(params.value)
        if (Number.isNaN(num)) {
          return '--'
        }
        return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      },
      cellStyle: { textAlign: 'right' },
      headerClass: 'ag-header-cell-right'
    }
  ], [])

  // Securities default column definition
  const securitiesDefaultColDef = useMemo(() => ({
    sortable: false, 
    filter: 'agTextColumnFilter',   
    resizable: true,
    suppressMenu: false, 
    floatingFilter: false,
    suppressHeaderMenuButton: false, 
    suppressHeaderFilterButton: false, 
    filterParams: {
      buttons: ['reset', 'apply'],
      closeOnApply: true
    }
  }), [])

  const gridApiRef = useRef<AgGridReact | null>(null)

  const onGridReady = useCallback((params: GridReadyEvent) => {
    if (gridApiRef) {
      (gridApiRef as any).current = { api: params.api }
    }
  }, [])


  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderTop: '2px solid #dee2e6', 
        borderRadius: 0, 
        overflow: 'hidden'
      }}
    >
      {/* Transaction Report Header - matching Summary Characteristics style */}
      <Box sx={{ 
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #e0e0e0',
        px: 2,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography 
          variant="h4" 
          sx={{ fontWeight: 'bold', color: '#000000' }}
        >
          Transaction Report
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" onClick={onSave}>
            Save What-If Portfolio
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const api = (gridApiRef as any)?.current?.api
              if (api?.exportDataAsExcel) {
                api.exportDataAsExcel()
              }
            }}
          >
            Export as Excel
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const api = (gridApiRef as any)?.current?.api
              if (api?.exportDataAsCsv) {
                api.exportDataAsCsv({
                  suppressQuotes: false,
                  columnSeparator: ',',
                  processCellCallback: (params: any) => {
                    const rawValue = params.node?.data?.[params.column?.colId]
                    if (typeof rawValue === 'number') {
                      return rawValue.toString()
                    }
                    if (typeof params.value === 'number') {
                      return params.value.toString()
                    }
                    return params.value
                  }
                })
              }
            }}
          >
            Export as CSV
          </Button>
        </Box>
      </Box>

      {/* Securities Grid */}
      <Box sx={{ 
        flex: 1,
        height: 'calc(100% - 50px)', 
        backgroundColor: 'white'
      }}>
        <div className={themeClassName} style={{ height: '100%', width: '100%' }}>
          <AgGridReact
            rowData={filteredSecurities}
            columnDefs={securitiesColumnDefs}
            defaultColDef={securitiesDefaultColDef}
            modules={[ClientSideRowModelModule]}
            animateRows={true}
            rowSelection="multiple"
            pagination={true}
            paginationPageSize={20}
            domLayout="normal"
            loading={loading}
            headerHeight={40}
            suppressColumnVirtualisation={true}
            suppressMenuHide={false}
            getMainMenuItems={(params) => {
              return ['filterMenuTab']
            }}
            onGridReady={onGridReady}
          />
        </div>
      </Box>
    </Box>
  )
}

export default TrasReport
